import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { type PlainPrompt, useDeletePrompt } from "@/queries/prompt-queries";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";
import { TrackLocations } from "@/hooks/use-segment";

type Props = {
    command: PlainPrompt;
    onClose: () => void;
};

export const DeleteCommandModal: FC<Props> = ({ command, onClose }) => {
    const deletePrompt = useDeletePrompt();
    const { toast } = useToast();

    const handleDelete = useCallback(async () => {
        try {
            await deletePrompt.mutateAsync({ promptId: command.id });
            toast({
                title: "Command deleted successfully",
                description: `/${command.spec?.command} has been removed from your organization`,
            });
            onClose();
        } catch (error) {
            console.error(error);
            toast({
                title: "Failed to delete command",
                description: formatError(error),
            });
        }
    }, [command.id, command.spec?.command, deletePrompt, toast, onClose]);

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                className="max-w-[500px]"
                data-testid="delete-command-modal"
                data-track-location={TrackLocations.DeleteCommandModal}
            >
                <DialogHeader>
                    <DialogTitle>Delete Agent Command</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete &quot;{command.metadata?.name}&quot;? This action cannot be
                        undone.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <div className="rounded-lg bg-surface-02 p-3">
                        <div className="font-mono text-sm text-content-secondary">/{command.spec?.command}</div>
                        <div className="mt-1 text-sm text-content-secondary">{command.metadata?.description}</div>
                    </div>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="secondary">Cancel</Button>
                    </DialogClose>
                    <Button
                        onClick={handleDelete}
                        variant="destructive"
                        loading={deletePrompt.isPending}
                        data-testid="confirm-delete-button"
                        data-tracking-id="delete-agent-command-delete-command-modal"
                    >
                        Delete Command
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
